// Chemin: C:\gestion-copro\src\components\mandat\MandatSuiviSection.tsx
// Widget de suivi des signatures du mandat de coordination
// Session 88 : création initiale
// Session 90 : Ajout bouton télécharger PDF mandat + bouton renvoyer notifications
// Session 91 : Passage des signataires (membresCS croisés avec signatures) au PDF
// Référence : SPECIFICATION_GOUVERNANCE_V2.md §5
//
// Ce composant s'intègre dans DashboardPage.tsx du coordinateur.
// Il affiche la progression des signatures et le détail par membre CS.
// Il ne s'affiche PAS si le mandat est déjà actif depuis plus de 7 jours.

import React, { useState, useEffect } from 'react';
import { useCopropriete } from '../../context/CoproprieteContext';
import { getMandatActif, envoyerNotificationMandat } from '../../services/mandat.service';
import { getMembresCSByCopropriete } from '../../services/conseilSyndical.service';
import { genererPDFMandat, SignatairePDF } from '../../services/pdfMandat.service';
import {
  MandatCoordination,
  getNbSignatures,
  getPourcentageSignatures,
  STATUT_MANDAT_LABELS,
  STATUT_MANDAT_COLORS
} from '../../types/mandat.types';
import { MembreCS, ROLE_CS_LABELS } from '../../types/conseilSyndical.types';
import {
  FileSignature,
  CheckCircle2,
  Clock,
  User,
  ShieldCheck,
  AlertCircle,
  Download,
  Send,
  Loader2
} from 'lucide-react';

const MandatSuiviSection: React.FC = () => {
  const { coproprieteId } = useCopropriete();

  const [loading, setLoading] = useState(true);
  const [mandat, setMandat] = useState<MandatCoordination | null>(null);
  const [membresCS, setMembresCS] = useState<MembreCS[]>([]);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [relancingNotif, setRelancingNotif] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!coproprieteId) {
        setLoading(false);
        return;
      }

      try {
        const mandatData = await getMandatActif(coproprieteId);
        setMandat(mandatData);

        if (mandatData) {
          const membres = await getMembresCSByCopropriete(coproprieteId, true);
          setMembresCS(membres);
        }
      } catch (err) {
        console.error('Erreur chargement mandat suivi:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [coproprieteId]);

  // Ne rien afficher si chargement
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-32"></div>
          </div>
        </div>
      </div>
    );
  }

  // Ne rien afficher si pas de mandat
  if (!mandat) {
    return null;
  }

  // Ne rien afficher si le mandat est déjà actif depuis plus de 7 jours
  if (mandat.statut === 'actif' && mandat.dateActivation) {
    const joursDepuisActivation = Math.floor(
      (Date.now() - mandat.dateActivation.toDate().getTime()) / (1000 * 60 * 60 * 24)
    );
    if (joursDepuisActivation > 7) {
      return null;
    }
  }

  // Ne rien afficher si révoqué ou expiré
  if (mandat.statut === 'revoque' || mandat.statut === 'expire') {
    return null;
  }

  const nbSignatures = getNbSignatures(mandat);
  const pourcentage = getPourcentageSignatures(mandat);
  const estActif = mandat.statut === 'actif';
  const enAttente = mandat.statut === 'en_attente_signatures';

  // Session 91 : Construire la liste des signataires pour le PDF
  // Croise les membresCS (noms) avec les signatures du mandat (statut)
  const construireSignatairesPDF = (): SignatairePDF[] => {
    // Filtrer : exclure le coordinateur (il ne signe pas son propre mandat)
    const membresSignataires = membresCS.filter((membre) => {
      const membreUserId = membre.userId || (membre as any).linkedUserId || '';
      return membreUserId !== mandat.coordinateurId;
    });

    return membresSignataires.map((membre) => {
      const signature = mandat.signatures[membre.id];
      return {
        membreCSId: membre.id,
        nom: `${membre.prenom} ${membre.nom}`,
        dateSignature: signature?.dateSignature || null,
        ip: signature?.ip || undefined,
      };
    });
  };

  // Session 90 : Télécharger le PDF du mandat
  const handleDownloadPDF = async () => {
    if (!mandat) return;
    setGeneratingPDF(true);
    try {
      const signataires = construireSignatairesPDF();
      const blob = genererPDFMandat({ mandat, signataires });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Mandat_Coordination_${mandat.nomCopropriete.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur génération PDF mandat:', error);
      alert('Erreur lors de la génération du PDF.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Session 90 : Relancer les notifications email
  const handleRelanceNotifications = async () => {
    if (!mandat || !coproprieteId) return;
    if (!window.confirm('Renvoyer les notifications email aux membres qui n\'ont pas encore signé ?')) return;
    
    setRelancingNotif(true);
    try {
      const result = await envoyerNotificationMandat(coproprieteId, mandat.id);
      if (result.envoyes > 0) {
        alert(`${result.envoyes} email(s) de relance envoyé(s).`);
      } else {
        alert('Aucun email envoyé (tous ont déjà signé ou pas de compte lié).');
      }
    } catch (error: any) {
      console.error('Erreur relance notifications:', error);
      alert('Erreur lors de l\'envoi des relances.');
    } finally {
      setRelancingNotif(false);
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border ${
      estActif ? 'border-green-200' : 'border-amber-200'
    } p-6`}>

      {/* En-tête */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            estActif ? 'bg-green-100' : 'bg-amber-100'
          }`}>
            {estActif ? (
              <ShieldCheck className="text-green-600" size={20} />
            ) : (
              <FileSignature className="text-amber-600" size={20} />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Mandat de coordination</h3>
            <p className="text-sm text-gray-500">
              Coordinateur : {mandat.coordinateurNom}
              {mandat.estMembreCS && (
                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                  Membre CS
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Badge statut */}
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
          STATUT_MANDAT_COLORS[mandat.statut]
        }`}>
          {STATUT_MANDAT_LABELS[mandat.statut]}
        </span>
      </div>

      {/* Barre de progression (seulement si en attente) */}
      {enAttente && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="text-gray-600">
              {nbSignatures} / {mandat.signaturesAttendues} signature{mandat.signaturesAttendues > 1 ? 's' : ''}
            </span>
            <span className="font-medium text-gray-900">{pourcentage}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2.5">
            <div
              className="bg-amber-500 h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${pourcentage}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Message de succès si mandat actif */}
      {estActif && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="text-green-600 flex-shrink-0" size={16} />
            <p className="text-sm text-green-800 flex-1">
              Mandat signé par tous les membres — copropriété opérationnelle.
              {mandat.dateActivation && (
                <span className="text-green-600 ml-1">
                  Activé le {mandat.dateActivation.toDate().toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Bouton télécharger PDF (toujours visible quand mandat existe) */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={handleDownloadPDF}
          disabled={generatingPDF}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {generatingPDF ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          {generatingPDF ? 'Génération...' : 'Télécharger le PDF du mandat'}
        </button>
      </div>

      {/* Alerte si en attente depuis longtemps */}
      {enAttente && mandat.dateCreation && (() => {
        const joursAttente = Math.floor(
          (Date.now() - mandat.dateCreation.toDate().getTime()) / (1000 * 60 * 60 * 24)
        );
        if (joursAttente >= 5) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="text-amber-600 flex-shrink-0" size={16} />
                <p className="text-sm text-amber-800 flex-1">
                  En attente depuis {joursAttente} jours. Pensez à relancer les membres qui n'ont pas encore signé.
                </p>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Bouton relance notifications (seulement si en attente) */}
      {enAttente && (
        <div className="mb-4">
          <button
            onClick={handleRelanceNotifications}
            disabled={relancingNotif}
            className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
          >
            {relancingNotif ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {relancingNotif ? 'Envoi en cours...' : 'Relancer les notifications par email'}
          </button>
        </div>
      )}

      {/* Liste des membres CS et leur statut de signature (coordinateur exclu) */}
      <div className="space-y-2">
        {membresCS
          .filter((membre) => {
            const membreUserId = membre.userId || (membre as any).linkedUserId || '';
            return membreUserId !== mandat.coordinateurId;
          })
          .map((membre) => {
          const signature = mandat.signatures[membre.id];
          const aSigne = !!signature;

          return (
            <div
              key={membre.id}
              className={`flex items-center justify-between p-3 rounded-lg ${
                aSigne ? 'bg-green-50' : 'bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  aSigne ? 'bg-green-200' : 'bg-gray-200'
                }`}>
                  {aSigne ? (
                    <CheckCircle2 className="text-green-700" size={16} />
                  ) : (
                    <User className="text-gray-500" size={16} />
                  )}
                </div>
                <div>
                  <p className={`text-sm font-medium ${
                    aSigne ? 'text-green-900' : 'text-gray-700'
                  }`}>
                    {membre.prenom} {membre.nom}
                  </p>
                  <p className="text-xs text-gray-500">
                    {ROLE_CS_LABELS[membre.role]}
                  </p>
                </div>
              </div>

              <div className="text-right">
                {aSigne ? (
                  <div>
                    <p className="text-xs font-medium text-green-700">Signé</p>
                    {signature.dateSignature && (
                      <p className="text-xs text-green-600">
                        {signature.dateSignature.toDate().toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short'
                        })}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-amber-600">
                    <Clock size={14} />
                    <span className="text-xs font-medium">En attente</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pied de page */}
      <p className="text-xs text-gray-400 mt-4 text-center">
        Base juridique : articles 1984 à 2010 du Code civil
      </p>
    </div>
  );
};

export default MandatSuiviSection;
